import {DatatablePage, Item, Items, Pagination, ResponseData} from 'app/models';
import {ActionCreator, ActionType} from "@ngrx/store";
import {IAppSettingsWrap, ICart, ICRUDCommand, ICRUDRecord, Id, IEventBlock, IOrderForm, IPage, IPageBase, IPlayerProps, IPreferencesBusiness, IPreferencesServer, IResource, IResourcePreview, ISceneBlock, ISceneTreeSearchResult, ISceneTreeSliceResults, IStationStatsV1, IUser, EnumStudioType, EnumPricingMethod} from "../interfaces";
import {on, ReducerTypes} from "@ngrx/store";
import {Subscription} from "rxjs";
import {take} from 'rxjs/operators';
import { HttpHeaders } from "@angular/common/http";
import {SelectionModel} from "@angular/cdk/collections";
import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {SceneMimeTypes} from "../containers/editor/scene-blocks/mime-constants";
import {IAccountDetails} from "../interfaces/enterprise-account-interface";
import {isBlankString, isNullOrUndefOrEmpty, isStringNullOrUndefOrEmpty, isTruthy} from './format';
import {Lib} from "../../libs/Lib";
import {PatchListener, produce} from "immer";
import {StoreSelectorsService} from "../services";
import {DEFAULT_DEV_SERVER, NONE_VALUE, SKIP_LOADING_ROUTES} from './global-constants';
import {isUndefined} from "lodash";
import * as _ from "lodash";

// const convert = require('xml-js');

import convert from "xml-js";
import { CreditCard } from './credit-card';
// import {isNumeric} from "rxjs/internal-compatibility";
// enablePatches();
// import {enableAllPlugins, enablePatches} from "immer";
// enableAllPlugins();

/**
 * Initialize DEV_TEST_PORT based on localStorage setting (dev mode only)
 * Returns 9443 (default dev port) or 443 (prod port) based on useDevServerPort setting
 */
export function getInitialDevPort(): number {
    // ALWAYS returns the PROD port (443) — the dev port (9443) is returned ONLY when BOTH:
    //   1. we are in dev mode / localhost (isHostDevOrLocal()), AND
    //   2. the user explicitly opted in via "use dev server port 9443" (useDevServerPort === '1').
    // In production isHostDevOrLocal() is false, so this can NEVER return 9443 there — even if a
    // stale useDevServerPort === '1' was synced into localStorage from a dev session. The checkbox
    // that sets it is likewise only shown in dev/local. Anything else (null/undefined/'0', wrong
    // host, parse error) falls through to 443.
    // Read the opt-in per BROWSER SESSION: an autologin ("open studio as this user") tab keeps its
    // config in sessionStorage (isolated per tab, see LocalStoreService), so it can run on a different
    // dev port than the reseller tab. sessionStorage therefore wins when present; a regular tab has no
    // sessionStorage config and falls back to the shared localStorage default (previous global behavior).
    try {
        if (typeof window !== 'undefined' && isHostDevOrLocal()) {
            const sessionConfig = window.sessionStorage.getItem('ILocalStorageConfig');
            const config = JSON.parse(sessionConfig ?? window.localStorage.getItem('ILocalStorageConfig'));
            if (config?.useDevServerPort === '1') {
                return 9443;
            }
        }
    } catch (e) {
        // Silently fall through to the prod port.
    }
    return 443;
}

/**
 * Data-grid row-line selection (shared so stations / scenes / resources behave IDENTICALLY).
 *
 * A selectable grid has two distinct click gestures:
 *   - checkbox click  -> additive multi-select TOGGLE (keeps every other checked row). The checkbox
 *                        must stopPropagation so its click never reaches the row handler.
 *   - row-line click  -> collapse to a SINGLE selection: deselect every other checked row and select
 *                        ONLY the clicked one. That is what this helper does.
 *
 * Uses SelectionModel.setSelection so the clear + select fires a single `changed` event.
 */
export function selectSingleRow<T>(selection: SelectionModel<T>, value: T): void {
    if (!selection || value === null || value === undefined) return;
    selection.setSelection(value);
}

export function formValueChanged(formGroup: FormGroup, cb: (v1) => void): Subscription {
    return formGroup.statusChanges
        .subscribe((result) => {
            if (result === 'VALID' && formGroup.pristine === false) {
                cb(result);
                formGroup.markAsPristine();
            }
        });
}

export function isSceneDesktopEmpty(i_scenePlayer: IPlayerProps): boolean {
    /** doesnt have all the segments so no good **/
    if (!i_scenePlayer?.Player?.Data?.["0"]?.Scene?.["0"]?.Players?.["0"]?.Player)
        return true;
    /** scene is empty and created in Desktop **/
    if (!i_scenePlayer?.Player?.Data?.["0"]?.Scene?.["0"]?.Players?.["0"])
        return true;
}

export function isSceneEmbedded(i_scenePlayer: IPlayerProps): boolean {
    /** scene with embedded (embedded not linked via src which we support ) scene created in Desktop **/
    if (!i_scenePlayer?.Player?.Data?.["0"]?.Scene?.["0"]?.Players?.["0"]?.Player) return false;
    return !!i_scenePlayer.Player.Data[0].Scene[0].Players[0].Player.find((p: ISceneBlock) => {
        /** 04-03-2022 ran into rare cases where no $ existed so just return false as this is not embedded  **/
        if (!p.$) return false;
        return p.$.player === '3510' && !p.$.src;
    });
}

/**
 * Check if a scene is a slider wrapper scene (top-level slider container)
 * Combines the three main checks: not empty, not private child, and mimeType is Slides
 * @param i_scenePlayer The scene player data to check
 * @returns true if the scene is a slider wrapper scene
 */
export function isMainTouchScene(i_scenePlayer: IPlayerProps): boolean {
    if (isSceneDesktopEmpty(i_scenePlayer)) return false;
    if (isTruthy(i_scenePlayer?.Player?.$?.private_child)) return false;
    return isTruthy(i_scenePlayer?.Player?.$?.mimeType === SceneMimeTypes.Slides);
}

export function isMimeTypeExists(mimes: Array<string>, mimeTarget: string): boolean {
    return mimes.findIndex((i_mime) => {
        const mimeShort = mimeSwap("longToShort", i_mime);
        const mimeLong = mimeSwap("shortToLong", i_mime);
        return (i_mime && i_mime === mimeTarget) || (mimeShort && mimeShort === mimeTarget) || (mimeLong && mimeLong === mimeTarget);
    }) > -1;
}

export function mimeSwap(from: 'shortToLong' | 'longToShort', value): string {
    let res = '';
    switch (from) {
        case "shortToLong": {
            res = SceneMimeTypes[value];
            break;
        }
        case "longToShort": {
            res = Object.keys(SceneMimeTypes).find((k) => {
                return SceneMimeTypes[k] === value;
            });
            break;
        }
    }
    // if (isNotNullUndefEmpty(res)) res = value;
    return res;
}

export function getCredentialsHeader(i_user: IUser, useHeaders = true): object {
    const credentials = Lib.EncryptUserPass(i_user.username, i_user.passwordNew);
    if (useHeaders) {
        return {
            headers: new HttpHeaders({'Content-Type': 'application/json', 'Authorization': credentials}),
            responseType: 'json' as 'json'
        };
    } else {
        return {
            authorization: credentials,
            responseType: 'json'
        };
    }
}

/** by pass cors from backend server **/
export function getCredentialsHeaderImage(i_user: IUser): object {
    const credentials = Lib.EncryptUserPass(i_user.username, i_user.passwordNew);
    return {
        headers: new HttpHeaders({'Content-Type': 'application/json', 'Authorization': credentials}),
        responseType: 'blob'
    };
}

export async function asyncTimeout(i_time) {
    return new Promise<string>(resolve => {
        setTimeout(() => {
            resolve('done');
        }, i_time);
    });
}


export function isSceneButNotSubScene(sceneItem: ISceneBlock): boolean {
    if (!sceneItem?.$?.player) return false;
    return compareIds(sceneItem.$.player, '3510') && !sceneItem.$.src;
}

export function isGroupItem(sceneItem: ISceneBlock): boolean {
    if (!sceneItem?.$?.player) return false;
    return compareIds(sceneItem.$.player, '3520');
}

/**
 * Turn a resource name into a per-instance block name ($.label). Sanitized to the XML/JSON-safe
 * alphanumeric+spaces charset so it survives the scene save pipeline. Used to seed $.label when a
 * resource is added to a scene (canvas drop, playlist drop, playlist add-content dialog).
 */
export function sanitizeResourceLabel(name: string): string {
    return (name || '')
        .replace(/[^a-zA-Z0-9 ]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

// A group keeps its children (and its design SIZE) in its own <Group> node — NOT <Scene>, because a group is
// not a schedulable scene. The design size (Data[0].Group[0].$.width/height) is the fixed canvas the children
// are authored against; it is kept separate from the group's outer Layout (the RENDERED box on the parent), so
// resizing the group on the scene scales the children instead of mutating them — exactly like a nested scene.
export function getGroupNode(group: ISceneBlock): any {
    return (group?.Data?.[0] as any)?.Group?.[0] || null;
}

// The group's design SIZE `$` (holds width/height), or null.
export function getGroupDesignBox(group: ISceneBlock): any {
    return getGroupNode(group)?.$ || null;
}

// The group's children array (design coordinates).
export function getGroupChildren(group: ISceneBlock): ISceneBlock[] {
    return getGroupNode(group)?.Players?.[0]?.Player;
}

// Set the group's design size. Creates the <Group> node / Players array if missing.
export function setGroupDesignBox(group: ISceneBlock, width: number, height: number): void {
    const data0: any = group?.Data?.[0];
    if (!data0) return;
    if (!data0.Group?.[0]) data0.Group = [{$: {}, Players: [{Player: []}]}];
    const node = data0.Group[0];
    node.$ = {...(node.$ || {}), width: String(Math.round(width)), height: String(Math.round(height))};
    if (!node.Players?.[0]) node.Players = [{Player: []}];
}

// A "container" of scene items is either a real Scene (also the shape of a playlist/collection page) or a
// Group. They hold their child players under different nodes; these helpers pick the right one so the generic
// structural code (add / remove / re-order / group / ungroup) works for both.
export function containerChildKey(item: ISceneBlock): 'Scene' | 'Group' {
    return isGroupItem(item) ? 'Group' : 'Scene';
}

// The child-players array of a container (scene or group).
export function getContainerPlayers(item: ISceneBlock): ISceneBlock[] {
    return (item?.Data?.[0] as any)?.[containerChildKey(item)]?.[0]?.Players?.[0]?.Player;
}

// The store path to a container's Players["0"] object, for actionUpdateSceneItem writes.
export function containerPlayersStorePath(item: ISceneBlock): string {
    return `Data["0"].${containerChildKey(item)}["0"].Players["0"]`;
}

export function isSubScene(sceneItem: ISceneBlock): boolean {
    if (!sceneItem || !sceneItem.$.player) return false;
    return compareIds(sceneItem.$.player, '3510') && !!sceneItem.$.src;
}

export function isResourceItem(sceneItem: ISceneBlock): boolean {
    if (!sceneItem || !sceneItem.$.player || !sceneItem.Data["0"].Resource) return false;
    return ['3100', '3130', '3140', '3134'].indexOf(sceneItem.$.player) > -1;
}

export function isLabelItem(sceneItem: ISceneBlock): boolean {
    if (!sceneItem || !sceneItem.$.player) return false;
    return compareIds(sceneItem.$.player, '3241');
}

/**
 * @method sceneItemInvalid:
 * in some strange cases StudioDesktop creates anomalies of scene items with no Layout[0], if so we skip them
 * add future anomalies here as well..
 **/
export function sceneItemInvalid(i_item): boolean {
    return !!_.isUndefined(i_item.Data[0].Layout);
}

export function playSound(i_value): HTMLAudioElement {
    switch (i_value) {
        case 'error': {
            const snd = new Audio("data:audio/wav;base64,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");
            snd.play();
            return snd;
        }
        case 'bleep': {
            const snd = new Audio('data:audio/ogg;base64,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');
            snd.play();
            return snd;
        }
        default: {
            const audio = new Audio();
            audio.src = `./assets/audio/ring${i_value}.mp3`;
            audio.load();
            audio.play();
            return audio;
        }
    }
}

export function uniqueActionName(segment, name) {
    return `[${segment.toUpperCase()}] ${name.toLowerCase()} ${_.uniqueId('action_')}`;
}

function traverse(o, func, p) {
    // tslint:disable-next-line:forin
    for (const i in o) {
        if (func.apply(this, [i, o[i], o])) {
            // res = o;
            break;
        }
        if (o[i] !== null && typeof (o[i]) === "object") {
            traverse(o[i], func, p);
        }
    }
}

export function sceneTreeWalk(i_allScenesData, i_func: (value: string, sceneItem: any, p: any) => void) {
    traverse(i_allScenesData, i_func, null);
}

export function traverseTree(i_treeData, i_func: (value: string, treeItem: any, p: any) => void) {
    traverse(i_treeData, i_func, null);
}

export function sceneTreeInjectIdsNotUsed(i_allScenesData, id): void {
    let p;

    function injectIds(key, value, o) {
        if (key === 'player') {
            switch (o.player) {
                case '4105': {
                    if (p.Data["0"].Fixed["0"]?.Page) {
                        p.Data["0"].Fixed["0"].Page.forEach((x) => {
                            if (x.$.type === 'scene' || x.$.type === 'resource')
                                x.$.id = _.uniqueId('__');
                        });
                    }
                    if (p.Data["0"].GPS["0"]?.Page) {
                        p.Data["0"].GPS["0"].Page.forEach((x) => {
                            if (x.$.type === 'scene' || x.$.type === 'resource')
                                x.$.id = _.uniqueId('__');
                        });
                    }
                    break;
                }
                case '4102': {
                    if (p.Data["0"].Playlist["0"]?.Page) {
                        p.Data["0"].Playlist["0"].Page.forEach((x) => {
                            if (x.$.type === 'scene' || x.$.type === 'resource')
                                x.$.id = _.uniqueId('__');
                        });
                    }
                    break;
                }
                case '3510': {
                    /**  if o.src exists we are dealing with an embedded scene **/
                    if (o.src) id = _.uniqueId('__');
                    o.id = id;
                    break;
                }
                default: {
                    o.id = _.uniqueId('__');
                }
            }
        }
        if (key === '$') p = o;
        return false;
    }

    traverse(i_allScenesData, injectIds, null);
}

/**
 * preProcessSceneTree
 *
 * Recursively processes a scene tree to:
 *   1. Inject unique IDs to each scene and component node
 *   2. Collect all event names found throughout the tree
 *
 * Scene Structure:
 *  - Scenes follow the IPlayerProps interface (see scenes-interfaces.ts)
 *  - Supports unlimited nesting depth via sub-scenes (child scenes)
 *  - Sub-scenes can be:
 *    a) Embedded: Full scene tree nested inline
 *    b) Referenced: Points to another scene via src property (src = scene_id)
 *
 * Tree traversal starts at the root scene and branches downward through all nested children
 * using the traverse() helper function for recursive deep-dive processing.
 *
 * @param i_allScenesData - The root scene data to process
 * @param id - The ID to assign to the root scene (sub-scenes get unique IDs)
 * @returns Array of unique event names found in the scene tree
 */
export function preProcessSceneTree(i_allScenesData: IPlayerProps, id: string): Array<string> {
    const events: Array<string> = [];
    const defaultFont = {
        fontSize: '24', fontColor: '16777215', fontFamily: 'Arial', fontWeight: 'normal',
        fontStyle: 'normal', textDecoration: 'none', textAlign: 'left', alignSelf: 'center',
        autoScaling: '1', wrap: '0'
    };

    function process(key: string, value: any, o: any): boolean {
        if (key && key.toLowerCase().indexOf('event') > -1) {
            findEvents(key, o, o[key], events, null);
        }
        /** Normalize Label: ensure it is an array and contains Font **/
        if (key === 'Label' && o.Label != null) {
            if (!Array.isArray(o.Label)) {
                o.Label = [o.Label];
            }
            if (o.Label[0] && !o.Label[0].Font) {
                o.Label[0].Font = [{ $: { ...defaultFont } }];
            }
            if (o.Label[0] && !o.Label[0].Text) {
                o.Label[0].Text = [''];
            }
        }
        if (key === 'player') {
            /** if player is a scene and we are interested in injecting ids **/
            if (o.player === '3510') {
                /**  if o.src exists we are dealing with an embedded scene **/
                if (o.src) id = _.uniqueId('__'); // o.src;
                o.id = id;
                o.mimeType = !isNullOrUndefOrEmpty(o.mimeType) ? o.mimeType : SceneMimeTypes.Player;
            } else {
                o.id = _.uniqueId('__');
            }
        }
        return false;
    }

    traverse(i_allScenesData, process, null);
    return [...new Set(events)];
}

/**
 * regenerateNestedItemIds
 *
 * After a copy/paste or duplicate the top-level block is given a fresh `$.id`, but every
 * descendant inside it keeps its original id. This affects any *container* block:
 *   - Group           — children under Data[0].Group[0].Players[0].Player[]
 *   - Playlist / Collection / Swiper / Loader / HourlyBalancer / GPS-Fixed — page
 *     references under their own `Page[]` array (each page has its own `$.id`)
 *   - embedded / nested sub-scenes
 * Pasting the same container several times then leaves multiple instances sharing
 * identical descendant ids, so any dispatch-by-id edit (drill-in label edit, resource
 * swap, playlist page edit, …) resolves to the wrong instance — in practice the last
 * matching occurrence in the tree.
 *
 * Rather than enumerate every container player-id, this is structure-driven: it walks the
 * whole block and re-issues a unique id to every attribute node (`$`) that is either a
 * component (`$.player`) or a page reference (`$.type` of 'scene' / 'resource'). The
 * caller-assigned top-level id is preserved (restored after the walk), so only descendants
 * get fresh ids.
 */
export function regenerateNestedItemIds(block: ISceneBlock): void {
    if (!block?.Data) return;
    const rootId = block?.$?.id;

    function injectIds(key, value, o): boolean {
        if (key === '$' && value) {
            if (value.player != null) {
                // a component / container node
                value.id = _.uniqueId('__');
            } else if ((value.type === 'scene' || value.type === 'resource') && value.id != null) {
                // a page reference inside a playlist / collection / swiper / loader / …
                value.id = _.uniqueId('__');
            }
        }
        return false;
    }

    traverse(block, injectIds, null);
    // keep the caller-assigned top-level id — only descendants get fresh ids
    if (rootId != null && block?.$) block.$.id = rootId;
}

export function sceneFindKey(i_allScenesData, i_lookKey: string, i_foundKey: (values) => void): void {
    const results = [];

    function findKey(key, value, o) {
        if (key === i_lookKey) {
            results.push({key, value, o});
        }
    }

    traverse(i_allScenesData, findKey, null);
    i_foundKey(results);
}

export function sceneFindEventInScenes(i_allScenesData, onlyString = false): Array<string> {
    const events: Array<string> = [];
    let lastSceneId = '-1';

    function process(key: string, value, o) {
        if (key && key.toLowerCase().indexOf('event') > -1) {
            findEvents(key, o, o[key], events, lastSceneId, onlyString);
        }
        if (key === 'player') {
            /** if player is a scene **/
            if (o.player === '3510') {
                lastSceneId = o.id;
            }
        }
        return false;
    }

    traverse(i_allScenesData, process, null);
    return [...new Set(events)];
}

/**
 * find event names in known keys such as clickEventName, eventName etc....
 * If lastSceneId arg was provided we return a more
 * complex data structure with additional data, otherwise
 * we just return the event string name that was found.
 */
function findEvents(key, o, eventValue, events, lastSceneId?, onlyString?): void {
    switch (key) {
        case 'EventCommand': {
            if (Array.isArray(eventValue)) {
                eventValue.forEach((v) => {
                    if (isBlankString(v.$.from)) return;
                    if (!onlyString && lastSceneId) {
                        events.push({key, value: v.$.from, o, lastSceneId});
                    } else {
                        events.push(v.$.from);
                    }
                });
            } else {
                // Lib.DevShow('found a non array EventCommand ' + eventValue);
            }
            break;
        }
        case 'clickEventName':
        case 'resultEventName':
        case 'eventName':
        case 'selectItemEventName': {
            if (isBlankString(eventValue)) return;
            if (!onlyString && lastSceneId) {
                events.push({key, value: eventValue, o, lastSceneId});
            } else {
                events.push(eventValue);
            }
            break;
        }
        case 'EventCommands': {
            // covered under EventCommand;
            break;
        }
        default: {
            // console.log(key, eventValue);
        }
    }
}

/**
 * preProcessScene
 *  inject IDs to src and id
 */
export function preProcessScene(i_scene: IPlayerProps, id: Id) {
    function process(key: string, value, o) {
        if (key === 'player') {
            /** if player is a scene and we are interested in injecting ids **/
            if (o.player === '3510') {
                /**  if o.src exists we are dealing with an embedded scene **/
                if (o.src) {
                    id = _.uniqueId('__'); // o.src;
                }
                o.id = id;
                o.mimeType = !isNullOrUndefOrEmpty(o.mimeType) ? o.mimeType : SceneMimeTypes.Player;
            } else {
                o.id = _.uniqueId('__');
            }
        }
        return false;
    }

    traverse(i_scene, process, null);
}

// Not used for now
// export function preProcessScene(i_scene: IPlayerProps, id: Id): Array<IEventBlock> {
//     const events: Array<IEventBlock> = [];
//     let lastEventId = null;

//     function process(key: string, value, o) {
//         if (key && key.toLowerCase().indexOf('event') > -1) {
//             parseEvent(key, o, o[key], events, {eventSceneId: id, eventBlockId: lastEventId});
//         }
//         if (key === 'player') {
//             /** if player is a scene and we are interested in injecting ids **/
//             if (o.player === '3510') {
//                 /**  if o.src exists we are dealing with an embedded scene **/
//                 if (o.src) {
//                     id = _.uniqueId('__'); // o.src;
//                     lastEventId = id;
//                 }
//                 o.id = id;
//                 o.mimeType = !isNullOrUndefOrEmpty(o.mimeType) ? o.mimeType : SceneMimeTypes.Player;
//             } else {
//                 o.id = _.uniqueId('__');
//                 lastEventId = o.id;
//             }
//         }
//         return false;
//     }

//     traverse(i_scene, process, null);
//     return events;
// }

export function getAllEventsInScenes(i_allScenesData, eventName = null): Array<IEventBlock> {
    const events: Array<IEventBlock> = [];
    let lastSceneId = '-1';
    let lastBlockId = null;
    let lastSceneName = '';
    let lastBlockName = '';
    let lastBlockPlayer = '';

    function process(key: string, value, o) {
        if (key && key.toLowerCase().indexOf('event') > -1) {
            parseEvent(key, o, o[key], events, {
                eventSceneId: lastSceneId,
                sceneName: lastSceneName,
                eventBlockId: lastBlockId,
                blockName: lastBlockName,
                eventBlockPlayer: lastBlockPlayer
            }, eventName);
        }
        if (key === 'player' && o.id && 'label' in o) {
            /** if player is a scene **/
            if (o.player === '3510') {
                if (!o.src) {
                    lastSceneId = o.id;
                    lastSceneName = o.label;
                }
            }
            lastBlockId = o.id;
            lastBlockName = o.label;
            lastBlockPlayer = o.player;
        }
        return false;
    }

    traverse(i_allScenesData, process, null);
    return events;
}

/**
 * find event names in known keys such as clickEventName, eventName etc....
 * If lastSceneId arg was provided we return a more
 * complex data structure with additional data, otherwise
 * we just return the event string name that was found.
 */
function parseEvent(key, o, eventValue, events: IEventBlock[], eventData, eventName = null): void {
    switch (key) {
        case 'EventCommand': {
            if (Array.isArray(eventValue)) {
                eventValue.forEach((v) => {
                    if (isBlankString(v.$.from)) return;
                    if (eventName && eventName !== v.$.from) return;
                    events.push({
                        eventType: key,
                        eventName: v.$.from,
                        ...eventData
                    });
                    // if (lastSceneId) {
                    //     events.push({key, value: v.$.from, o, lastSceneId});
                    // } else {
                    //     events.push(v.$.from);
                    // }
                });
            } else {
                // Lib.DevShow('found a non array EventCommand ' + eventValue);
            }
            break;
        }
        case 'clickEventName':
        case 'resultEventName':
        case 'eventName':
        case 'selectItemEventName': {
            if (isBlankString(eventValue)) return;
            if (eventName && eventName !== eventValue) return;
            events.push({
                eventType: key,
                eventName: eventValue,
                ...eventData
            });
            // if (lastSceneId) {
            //     events.push({key, value: eventValue, o, lastSceneId});
            // } else {
            //     events.push(eventValue);
            // }
            break;
        }
        case 'EventCommands': {
            // covered under EventCommand;
            break;
        }
        default: {
            // console.log(key, eventValue);
        }
    }
}


/**
 * getPlayerIdInScene
 *
 * Retrieves a specific player block from within a scene tree by its ID.
 * Falls back to scene-level data if the player ID is not found (e.g., if player was deleted).
 *
 * @param i_allScenesData - Array of scene data to search through
 * @param i_sceneId - The scene ID to search within
 * @param i_playerId - The player/block ID to find
 * @returns Object containing the sceneId and the found scene item (or null if not found)
 */
export function getPlayerIdInScene(i_allScenesData, i_sceneId: Id, i_playerId: Id): ISceneTreeSliceResults {
    let res = null;
    let resScene = null;
    let p;

    function lookUpId(key, value, o) {
        if (key === 'player' && o.id === i_playerId) {
            res = {o, p};
            return true;
        }
        /** Fallback: capture scene-level data in case playerId is not found (e.g., player was deleted) **/
        if (key === 'player' && o.id === i_sceneId) {
            resScene = {o, p};
        }
        if (key === '$') p = o;
    }

    for (const index in i_allScenesData) {
        if (i_allScenesData[index]['Player'].$.id === i_sceneId) {
            traverse(i_allScenesData[index], lookUpId, null);
            break;
        }
    }
    if (res == null) {
        if (resScene == null && isHostDevOrLocal()) {
            throw new Error('Warning, could not find getPlayerIdInScene, could not find your query in store...');
        }
        /** fall back to scene selection as did not find playerId we were searching for **/
        res = resScene;
    }
    return {sceneId: i_sceneId, sceneItem: res ? res.p : null};
}

/**
 * Build an IN-MEMORY scene that renders ONLY one component (used by the focused component-edit mode for a
 * component embedded in a playlist page). The wrapper clones the real scene's Data (so background/layout are
 * preserved) but replaces the scene's top-level Players with just the target component block, and KEEPS
 * Player.$.id === realSceneId. Because the wrapper's scene id equals the real scene id, every canvas edit
 * dispatches (realSceneId, componentId) and the reducer's deep getPlayerIdInScene writes straight back to the
 * real nested component - no second store scene, no mirroring. Returns null if the scene/component is missing.
 */
/**
 * Build the breadcrumb crumbs for the selected scene item's path WITHIN a scene (the part after the scene
 * crumb). A top-level scene item -> [item]. A component inside a playlist page -> [playlist, component], where
 * the component crumb carries the playlist region so clicking it re-enters focused component edit. Returns []
 * when nothing item-level is selected (e.g. the scene itself).
 */
// container blocks whose pages embed a child player directly (drill-in targets). Each keeps its pages under
// Data[0].<name>[0].Page. Playlist, Collection, Flicking, Music-list and HourlyBalancer all share this shape,
// so a resource/component drilled into from any of them resolves to a "scene > container > item" breadcrumb.
const PAGE_CONTAINER_KEYS = ['Playlist', 'Collection', 'Flicking', 'Music', 'HourlyBalancer'];

export function getSceneItemPathCrumbs(i_scene, i_selectedItemId, i_sceneId): Array<{ sceneId: any, label: string, sceneItemId: any, regionWidth?: any, regionHeight?: any }> {
    if (!i_scene || !i_selectedItemId || i_selectedItemId == i_sceneId) return [];
    const players = i_scene?.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player || [];
    return getItemPathCrumbsIn(players, i_selectedItemId, i_sceneId);
}

// The crumbs for an item within a list of players. Recurses into Groups (a Group holds its children under the
// same Data[0].Scene[0].Players[0] path as a scene), so a group nested in a group yields the full trail
// "scene > Group > Group2" and each crumb can drill back into its own level.
function getItemPathCrumbsIn(i_players, i_selectedItemId, i_sceneId): Array<{ sceneId: any, label: string, sceneItemId: any, regionWidth?: any, regionHeight?: any }> {
    for (const p of i_players || []) {
        if (p?.$?.id == i_selectedItemId) {
            return [{ sceneId: i_sceneId, label: p.$.label || 'item', sceneItemId: i_selectedItemId }];
        }
        const groupChildren = getGroupChildren(p);
        if (groupChildren) {
            const inner = getItemPathCrumbsIn(groupChildren, i_selectedItemId, i_sceneId);
            if (inner.length) {
                return [{ sceneId: i_sceneId, label: p.$.label || 'group', sceneItemId: p.$.id }, ...inner];
            }
        }
        for (const containerKey of PAGE_CONTAINER_KEYS) {
            const pages = p?.Data?.[0]?.[containerKey]?.[0]?.Page || [];
            for (const page of pages) {
                const comp = page?.Player?.[0];
                if (comp?.$?.id == i_selectedItemId) {
                    const region = p?.Data?.[0]?.Layout?.[0]?.$;
                    return [
                        { sceneId: i_sceneId, label: p.$.label || 'playlist', sceneItemId: p.$.id },
                        { sceneId: i_sceneId, label: comp.$.label || page?.$?.page || 'item', sceneItemId: i_selectedItemId, regionWidth: region?.width, regionHeight: region?.height },
                    ];
                }
            }
        }
    }
    return [];
}

/**
 * Find a scene item by id anywhere in a players tree, descending into Group children (which live under
 * Data[0].Group[0].Players[0]) AND into the players embedded in Playlist/Collection pages. The top-level
 * `players` array alone is not enough once containers nest: a group inside a group, or a group sitting in a
 * playlist page, is only reachable this way.
 */
export function findSceneItemDeep(i_players: any[], i_itemId): any {
    for (const p of i_players || []) {
        if (p?.$?.id == i_itemId) return p;
        const nested = [
            ...(getGroupChildren(p) || []),
            ...[...(p?.Data?.[0]?.Playlist?.[0]?.Page || []), ...(p?.Data?.[0]?.Collection?.[0]?.Page || [])]
                .map((page) => page?.Player?.[0])
                .filter((player) => !!player),
        ];
        if (nested.length) {
            const found = findSceneItemDeep(nested, i_itemId);
            if (found) return found;
        }
    }
    return null;
}

export function buildComponentWrapperScene(i_allScenesData, i_realSceneId: Id, i_componentId: Id, i_region?: { width: any, height: any }, i_fitToOrigin?: boolean, i_groupEdit?: boolean): IPlayerProps {
    if (!i_allScenesData) return null;
    let realScene = null;
    for (const index in i_allScenesData) {
        if (i_allScenesData[index]?.Player?.$?.id == i_realSceneId) {
            realScene = i_allScenesData[index];
            break;
        }
    }
    if (!realScene?.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]) return null;
    let componentBlock = null;
    try {
        componentBlock = getPlayerIdInScene(i_allScenesData, i_realSceneId, i_componentId)?.sceneItem;
    } catch {
        return null;
    }
    if (!componentBlock) return null;

    const wrapperData = _.cloneDeep(realScene.Player.Data[0]);

    if (i_groupEdit) {
        // drilling INTO a Group (player 3520) to edit its children: render the group's CHILDREN as the wrapper's
        // top-level players, so each becomes an individually selectable/editable moveable. The wrapper keeps the
        // real scene id, so every child edit dispatches (realSceneId, childId) and the reducer's deep id-resolve
        // writes it straight back into the group's <Group> node. Children keep their group-relative coordinates
        // and the canvas is sized to the group, so positions line up 1:1 with how they render inside the group.
        const children = getGroupChildren(componentBlock);
        // Size the drill-in canvas to the group's fixed DESIGN size (Data[0].Group[0].$.width/height) - the size
        // children are authored against - so they line up 1:1 and editing happens at design scale (never at the
        // scaled/rendered size the group takes on the parent). Fall back to the playlist region for a group used
        // as a PAGE (which has no design size of its own — it fills the slot).
        const designLayout = getGroupDesignBox(componentBlock);
        const groupLayout = (designLayout?.width && designLayout?.height) ? designLayout : i_region;
        if (wrapperData.Scene?.[0]?.Players?.[0]) {
            // an EMPTY group may carry no children array at all (an empty <Players/> round-trips from XML
            // without a Player key) -> fall back to [], so the canvas opens empty. Leaving the wrapper's
            // players untouched would render the WHOLE real scene inside the group-sized canvas.
            wrapperData.Scene[0].Players[0].Player = _.isArray(children) ? _.cloneDeep(children) : [];
        }
        if (groupLayout && wrapperData.Layout?.[0]?.$) {
            wrapperData.Layout[0].$ = { ...wrapperData.Layout[0].$, x: '0', y: '0', width: String(groupLayout.width), height: String(groupLayout.height) };
        }
        return { Player: { $: _.cloneDeep(realScene.Player.$), Data: [wrapperData] } } as IPlayerProps;
    }

    wrapperData.Scene[0].Players[0].Player = [_.cloneDeep(componentBlock)];
    const compLayout = wrapperData.Scene?.[0]?.Players?.[0]?.Player?.[0]?.Data?.[0]?.Layout?.[0]?.$;
    if (i_fitToOrigin && compLayout && wrapperData.Layout?.[0]?.$) {
        // drilling into a top-level scene component: size the canvas to the component's OWN dimensions and move
        // the component to the origin, so it opens BY ITSELF (fills the canvas) instead of appearing as a speck
        // at its original scene position. Both wrapper builds (overlay + SignLib render) must pass this flag so
        // the moveable overlay and the rendered pixels stay in sync.
        wrapperData.Layout[0].$ = { ...wrapperData.Layout[0].$, x: '0', y: '0', width: String(compLayout.width), height: String(compLayout.height) };
        compLayout.x = '0';
        compLayout.y = '0';
    } else if (i_region?.width && i_region?.height && wrapperData.Layout?.[0]?.$) {
        // size the canvas to the parent playlist region (the slot the component renders in at runtime), so the
        // component appears at its true in-playlist scale instead of as a speck on the full scene canvas
        wrapperData.Layout[0].$ = { ...wrapperData.Layout[0].$, x: '0', y: '0', width: String(i_region.width), height: String(i_region.height) };
    }
    return { Player: { $: _.cloneDeep(realScene.Player.$), Data: [wrapperData] } } as IPlayerProps;
}

/**
 get slice array of multiple scenes who match our scene id we are searching for
 look in
 1. embedded scenes / o.src = ''
 2. in playlists & location based
 3. scene table
 **/
export function getPlayerIdsOfSceneId(i_allScenesData, i_sceneId: Id): ISceneTreeSearchResult[] {
    const collection: ISceneTreeSearchResult[] = [];
    let lastPlaylistId = null;
    let lastLocationId = null;
    let lastPlayId = null;
    let lastSceneId = null;
    let lastParent = null;

    function lookUpId(key, value, o) {
        if (key === 'src' && o.src === i_sceneId) {
            if (lastParent.$.autoScaling) {
                lastPlaylistId = null;
                lastLocationId = null;
            }
            collection.push({
                sceneItemId: null,
                sceneId: lastSceneId,
                key,
                value,
                o,
                lastParent,
                lastPlaylistId,
                lastLocationId
            });
        }
        if (key === '$') lastParent = o;

        /** remember last **/
        if (key === 'player') {
            switch (o.player) {
                case '3510': {
                    /** only remember non embedded scenes **/
                    if (o.src) break;
                    lastSceneId = o.id;
                    lastPlaylistId = null;
                    lastLocationId = null;
                    lastParent = null;
                    break;
                }
                case '4105': {
                    lastLocationId = o.id;
                    break;
                }
                case '4102': {
                    lastPlaylistId = o.id;
                    break;
                }
                default: {
                    lastPlayId = o.id;
                    break;
                }
            }
        }
    }

    // tslint:disable-next-line:forin
    for (const index in i_allScenesData) {
        traverse(i_allScenesData[index], lookUpId, null);
    }
    return collection;
}

/**
 get slice array of multiple resources who match our scene id we are searching for
 look in
 1. inside scenes
 2. in playlists
 3. in resource table
 **/
export function getPlayerIdsOfResourceId(i_allScenesData, i_resourceId: Id): ISceneTreeSearchResult[] {
    const collection: ISceneTreeSearchResult[] = [];
    let lastPlaylistId = null;
    let lastLocationId = null;
    let lastImageId = null;
    let lastPlayId = null;
    let lastSceneId = null;
    let lastParent = null;

    function lookUpId(key, value, o) {
        if (key === 'resource' && o.resource === i_resourceId) {
            if (lastParent.AspectRatio) {
                lastPlaylistId = null;
                lastLocationId = null;
            }
            collection.push({
                sceneItemId: lastPlayId,
                sceneId: lastSceneId,
                key,
                value,
                o,
                lastParent,
                lastPlaylistId,
                lastLocationId,
                lastImageId
            });
        }
        if (key === '$') lastParent = o;

        /** remember last **/
        if (key === 'player') {
            switch (o.player) {
                case '3510': {
                    /** we do not want to remember last scene id if its an embedded scene **/
                    if (o.src) break;
                    lastSceneId = o.id;
                    lastPlaylistId = null;
                    lastLocationId = null;
                    lastParent = null;
                    break;
                }
                case '4105': {
                    lastLocationId = o.id;
                    break;
                }
                case '4102': {
                    lastPlaylistId = o.id;
                    break;
                }
                case '3130':
                case '3140':
                case '3100': {
                    lastImageId = o.id;
                    break;
                }
                default: {
                    lastPlayId = o.id;
                    break;
                }
            }
        }
    }

    // tslint:disable-next-line:forin
    for (const index in i_allScenesData) {
        traverse(i_allScenesData[index], lookUpId, null);
    }
    return collection;
}

export function doNothing() {
}

/** server does not like saving single comma **/
export function restrictSingleQuote(i_value): string {
    if (isNullOrUndefOrEmpty(i_value)) return i_value;
    return i_value.replace(/'/g, '`');
}

export function restrictXml(i_value): string {
    if (isNullOrUndefOrEmpty(i_value)) return i_value;
    return Lib.ConvertEncode(i_value, 'COMPATIBLE');
}

export function getKeyCode(char) {
    const keyCode = char.charCodeAt(0);
    if (keyCode > 90) {  // 90 is keyCode for 'z'
        return keyCode - 32;
    }
    return keyCode;
}

export function avoidPassCharacters(asString = true): any {
    const chars = ['&', '#', '{', '}', '&', '\'', '?', ':', '+', '\\', ',', '/', '`'];
    if (asString) {
        return ' ' + chars.join('  ') + ' ';
    } else {
        return chars;
    }
}

export function avoidLoginCharacters(asString = true): any {
    const chars = ['?', '|', '/', '\\', '{', '~', '[', '}', ']', '"', '\'', '`', '*', '(', ')', '%', '^', '<', '>', ',', '!', '&', '#', '$', ':', ';'];
    if (asString) {
        return ' ' + chars.join('  ') + ' ';
    } else {
        return chars;
    }
}


export function restrictChar(event, mode): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    switch (mode) {
        /** only disallow ' **/
        case 'basic0':
        case 'basic1': {
            return !(charCode === 39);
        }
        case 'basic2': {
            /** only alphanumeric space _ - **/
            if (charCode === 95 || charCode === 45) return true;
            return ((charCode > 64 && charCode < 91) || (charCode > 96 && charCode < 123) || charCode == 8 || charCode == 32 || (charCode >= 48 && charCode <= 57));
        }
        case 'fieldName': {
            /** only alphanumeric and underscore (a-z A-Z 0-9 _) **/
            if (charCode === 95) return true; // underscore
            if (charCode === 8) return true; // backspace
            return ((charCode > 64 && charCode < 91) || (charCode > 96 && charCode < 123) || (charCode >= 48 && charCode <= 57));
        }
        case 'basic3': {
            /** only disallow ' or carriage return **/
            return !(charCode === 39 || charCode === 13);
        }
        case 'password': {
            const avoidChars = avoidPassCharacters(false);
            const f = avoidChars.find((i_char: any) => i_char.charCodeAt(0) === charCode);
            return !!isUndefined(f);
        }
        case 'login': {
            const avoidChars = avoidLoginCharacters(false);
            const f = avoidChars.find((i_char: any) => i_char.charCodeAt(0) === charCode);
            return !!isUndefined(f);
        }
        // case 'login': {
        //     /** nothing fancy, but do allow space **/
        //     return [63, 124, 47, 92, 123, 126, 91, 125, 93, 34, 39, 96, 42, 40, 41, 37, 94, 60, 62, 44, 33, 38, 35, 36].indexOf(charCode) === -1;
        // }
        case 'xmlCompatible': {
            /** dont allow < > & " ' **/
            return [60, 62, 38, 34, 39, 63].indexOf(charCode) === -1;
        }
        case 'numbers': {
            /** only numbers **/
                // return !(charCode > 31 && (charCode < 48 || charCode > 57));
            const inp = String.fromCharCode(charCode);
            return /[0-9]/.test(inp);
        }
        case 'decimals': {
            /** only numbers with decimal **/
            return !(charCode != 46 && charCode > 31 && (charCode < 48 || charCode > 57));

        }
        case 'alphabets': {
            /** alphabets **/
            const inp = String.fromCharCode(charCode);
            // return /[a-zA-Z0-9]/.test(inp);
            return /[a-zA-Z]/.test(inp);
        }
        case 'alphanums': {
            /** alphabets and number **/
            const inp = String.fromCharCode(charCode);
            return /^[a-zA-Z0-9_]*$/.test(inp);
        }
        case 'none': {
            return true;
        }
        /** disallow carriage return and ' & \ < > and others **/
        default: {
            if (charCode === 37) return false;
            if (charCode === 35) return false;
            if (charCode === 13) return false;
            if (charCode === 38) return false;
            if (charCode === 39) return false;
            if (charCode === 92) return false;
            if (charCode === 60) return false;
            if (charCode === 62) return false;
            if (charCode === 63) return false;
            return true;
        }
    }
}

export function removeUndefNulls(value: string | Array<any> | object): any {
    if (_.isUndefined(value) || _.isNull(value)) return '';
    if (_.isArray(value)) {
        return value.map((v) => {
            if (_.isUndefined(v) || _.isNull(value)) v = '';
        });
    }
    Object.keys(value).forEach((v) => {
        if (_.isUndefined(value[v]) || _.isNull(value[v])) value[v] = '';
    });
    return value;
}

export function validateEmail(email: string): boolean {
    if (isNullOrUndefOrEmpty(email)) return false;
    const extendedAscii = /[^\x00-\x7F]/g;
    const matches = email.match(extendedAscii);
    if (matches) return false;
    const re = /^(([^<>()\[\]\\.,;:\s@']+(\.[^<>()\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}

export function validateUrl(url): string | null {
    if (isNullOrUndefOrEmpty(url)) return null;
    if (url.indexOf('http') === -1) {
        url = `https://${url}`;
    }
    const pattern = `(\\b(https?|http|ldap)://)?[-A-Za-z0-9+&@#/%?=~_|!:,.;]+[-A-Za-z0-9+&@#/%=~_|]`;
    const re: any = new RegExp(pattern);
    const match = url.match(re);
    if (match && match[1]) return url;
    return null;
}

export function shortenString(i_value: string, strLength: number, prepend = ' ...') {
    if (!i_value) return '';
    if (i_value.length < strLength)
        return i_value;
    const v = i_value.substr(0, strLength).toString() + prepend;
    return v;
}

export function dataIsSuccess(data: ResponseData): boolean {
    return true;
    // return data.status === DATA_STATUS_SUCCESS;
}

export function concatIdBusinessId(id, businessId, version): string {
    if (!version) version = 1;
    return `${id}_${businessId}_${version}`;
}

export function getDefaultStationStats(stationBusinessId: Id, stationId: Id, version: Id): IStationStatsV1 {
    return {
        $: {
            station_business_id: stationBusinessId,
            id: stationId,
            name: `player ${_.uniqueId()}`,
            watchDogConnection: '',
            status: '',
            runningTime: '',
            os: '',
            caching: '',
            lastUpdate: '',
            totalMemory: '',
            peakMemory: '',
            appVersion: '',
            airVersion: '',
            localAddress: '',
            socket: '',
            connection: '',
            cameraStatus: '',
            lastCameraTest: '',
            businessId: '',
            source: '',
            localIp: '',
            publicIp: '',
            startTime: '',
            tree_path: '',
            geoLocation: null,
            version
        }
    };
}


// export function produceOn<Type extends string, C extends FunctionWithParametersType<any, object>, State, Creators extends ActionCreator>(
//     actionType: ActionCreator<Type, C>,
//     callback: (draft: Draft<State>, action: ActionType<ActionCreator<Type, C>>) => any,
//     listener?: PatchListener,
// ): ReducerTypes<State, Creators[]> {
//     return on(
//         actionType, 
//         (state: State, action): State => produce(state, (draft: Draft<State>) => callback(draft, action), listener)
//     ) as unknown as ReducerTypes<State, Creators[]>;
// }

export function produceOn<State, Creators extends ActionCreator>(
    creators: Creators,
    callback: (state: State, action: ActionType<Creators>) => void,
    listener?: PatchListener,
): ReducerTypes<State, Creators[]> {
    return on(
        creators,
        (state, action) => produce(state, draft => callback(draft as State, action), listener) as State,
    ) as unknown as ReducerTypes<State, Creators[]>;
}

export function createScenePreviewUrl(coreAppData: IAppSettingsWrap, businessId, sceneId, isFullFeatures = null, overrideWebToken: string = null): string {
    const devFlag = '';
    let server = '';
    /** if we are running locally or in dev mode or in full feature mode or from dev.signage.me we will open dev Web Player **/
    if (isFullFeatures || Lib.DevMode() || isHostDev() || isHostLocal()) {
        server = DEFAULT_DEV_SERVER;
        if (isFullFeatures) {
            // devFlag = '&dev=1';
        }
    } else {
        server = coreAppData[1].masterServer;
    }
    if (!businessId) {
        businessId = coreAppData[0].businessId;
    }
    const token = overrideWebToken || coreAppData[0].webToken;
    return `https://${server}/signplayer?domain=${coreAppData[1].masterServer}&businessId=${businessId}&sceneId=${sceneId}&token=${token}${devFlag}`;
}

export function getResourcePreview(i_resource: IResource, i_coreAppData: IAppSettingsWrap, useGPU: boolean, overrideBusinessId: number, useProxy: boolean): IResourcePreview {
    const res_type = i_resource?.resource_type ? i_resource.resource_type.toLowerCase() : "";
    let businessId = i_coreAppData[0].businessId;
    if (overrideBusinessId !== -1) {
        businessId = overrideBusinessId as any;
    }
    /** do a redirect request via web proxy service **/
    switch (res_type) {
        case "svg":
        case "png":
        case "jpg":
        case "gif":
        case "jpeg": {
            let url;
            /**
             see more: userService.getProxyResource(i_value)...
             url = `${i_coreAppData[0].webApiBaseUrl}/Web/redirectRequest.ashx?url=${i_coreAppData[1].cloudResourcesS3}/business${i_coreAppData[0].businessId}/resources/${i_resource.resource_id}.${i_resource.resource_type}`;
             url = 'https://earth.signage.me/Web/redirectRequest.ashx?url=https://s3-us-west-2.amazonaws.com/oregon-signage-resources/business391060/resources/405.jpg';
             console.log('force proxy ' + url);
             // proxy examples for gpu and non-gpu urls:
             https://galaxy.signage.me/Web/redirectRequest.ashx?url=https://s3-us-west-2.amazonaws.com/oregon-signage-resources/business391060/resources/405.jpg
             https://galaxy.signage.me/Web/redirectRequest.ashx?url=https://oregon-signage-resources.s3-us-west-2.amazonaws.com/business1000/resources/scenes_snaps/Account1.png
             **/
            if (useGPU && useProxy) {
                const urlGpu = `${i_coreAppData[1].cloudResourcesGPU}/business${i_coreAppData[0].businessId}/resources/${i_resource.resource_id}.${i_resource.resource_type}`;
                url = `${i_coreAppData[1].protocol}${i_coreAppData[1].masterServer}/Web/redirectRequest.ashx?url=${urlGpu}`;
            } else if (!useGPU && useProxy) {
                const urlS3 = `${i_coreAppData[1].cloudResourcesS3}/business${i_coreAppData[0].businessId}/resources/${i_resource.resource_id}.${i_resource.resource_type}`;
                url = `${i_coreAppData[1].protocol}${i_coreAppData[1].masterServer}/Web/redirectRequest.ashx?url=${urlS3}`;
            } else if (useGPU && !useProxy) {
                url = `${i_coreAppData[1].cloudResourcesGPU}/business${i_coreAppData[0].businessId}/resources/${i_resource.resource_id}.${i_resource.resource_type}`;
            } else {
                url = `${i_coreAppData[1].cloudResourcesS3}/business${i_coreAppData[0].businessId}/resources/${i_resource.resource_id}.${i_resource.resource_type}`;
            }
            // console.log('getResourcePreview v3 using ' + url);
            return {
                icon: 'image',
                type: 'image',
                url,
                byte: i_resource.resource_bytes_total
            };
        }
        case "swf": {
            return {
                type: 'flash',
                icon: 'flash_on',
                url: `./assets/images/no_image_flash.png`,
                byte: i_resource.resource_bytes_total
            };
        }
        case "mp3": {
            return {
                type: 'sound',
                icon: 'play',
                url: `./assets/images/no_image_sound.png`,
                byte: i_resource.resource_bytes_total
            };
        }
        case "flv": {
            return {
                type: 'flash_video',
                icon: 'play',
                url: `./assets/images/no_image_film.png`,
                byte: i_resource.resource_bytes_total
            };
        }
        case "mp4":
        case "m4v":
        case "webm": {
            return {
                type: 'video',
                icon: 'video-camera',
                videoUrl: `${i_coreAppData[1].cloudResourcesGPU}/business${i_coreAppData[0].businessId}/resources/${i_resource.resource_id}.${i_resource.resource_type}`,
                url: `./assets/images/no_image_video.png`,
                byte: i_resource.resource_bytes_total
            };
        }
        default: {
            return {
                type: 'none',
                icon: 'block',
                url: `./assets/images/no_image_null.png`,
                byte: i_resource?.resource_bytes_total || '0'
            };
        }
    }
}

export function createResourceUrl(coreAppData: IAppSettingsWrap, businessId = null, resourceId = null, resourceType = null, resource: IResource = null) {
    if (!coreAppData) return "./assets/images/no_image_null.png";

    if (!businessId || !resourceId) {
        businessId = (resource?.shortcut_business_id && resource?.shortcut_business_id != "0") ? resource.shortcut_business_id : coreAppData[0].businessId;
        resourceId = (resource?.shortcut_resource_id && resource?.shortcut_resource_id != "0") ? resource.shortcut_resource_id : resource?.resource_id;
    }
    if (!resourceType) {
        resourceType = resource?.resource_type;
    }

    if (!businessId || !resourceId) return "./assets/images/no_image_null.png";
    return `${coreAppData[1].cloudResourcesS3}/business${businessId}/resources/${resourceId}.${resourceType}`;
}


export function getResourceFormat(resource): IResourcePreview {
    const resourceType = resource.resourceType || resource.resource_type;
    switch (resourceType) {
        case "svg":
        case "png":
        case "gif":
        case "jpg":
        case "jpeg": {
            return {
                icon: 'image',
                type: 'image',
                url: resource.url
            };
        }
        case "swf": {
            return {
                type: 'flash',
                icon: 'flash_on',
                url: `./assets/images/no_image_flash.png`
            };
        }
        case "mp3": {
            return {
                type: 'sound',
                icon: 'play',
                url: `./assets/images/no_image_sound.png`
            };
        }
        case "flv": {
            return {
                type: 'flash_video',
                icon: 'play',
                url: `./assets/images/no_image_film.png`
            };
        }
        case "mp4":
        case "m4v":
        case "webm": {
            return {
                type: 'video',
                icon: 'video-camera',
                videoUrl: resource.url,
                url: `./assets/images/no_image_video.png`
            };
        }
        default: {
            return {
                type: 'none',
                icon: 'block',
                url: `./assets/images/no_image_null.png`
            };
        }
    }
}

export function parseJSON(string: string, defaultValue: any = false): any {
    try {
        return JSON.parse(string);
    } catch (e) {
        return defaultValue;
    }
}


export function convertPaginationToDatatablePage(pagination: Pagination): DatatablePage {
    const page = new DatatablePage();

    page.count = pagination.totalCount;
    page.pageSize = pagination.perPage;
    page.limit = pagination.perPage;
    page.offset = pagination.currentPage - 1;

    return page;
}

export function equalValueValidator(targetKey: string, toMatchKey: string): ValidatorFn {
    return (group: FormGroup): { [key: string]: any } => {
        const target = group.controls[targetKey];
        const toMatch = group.controls[toMatchKey];
        if (_.isEmpty(target.value) || _.isEmpty(toMatch.value)) {
            toMatch.setErrors({equalValue: targetKey});
            const message = 'password required';
            return {'equalValue': message};
        }
        if (target.touched && toMatch.touched) {
            const isMatch = target.value === toMatch.value;
            // set equal value error on dirty controls
            if (!isMatch && target.valid && toMatch.valid) {
                toMatch.setErrors({equalValue: targetKey});
                const message = targetKey + ' != ' + toMatchKey;
                return {'equalValue': message};
            }
            if (isMatch && toMatch.hasError('equalValue')) {
                toMatch.setErrors(null);
            }
        }
        return null;
    };
}

export function convertObjectToItems(obj: object): Items {
    return Object.keys(obj).map(key => {
        const item = new Item();

        item.name = key;
        item.value = obj[key];

        return item;
    });
}

export function convertModelToFormData(model: object, formName?: string | undefined, formData?: FormData | undefined): FormData {
    formData = formData || new FormData();
    convertObjectToItems(model).forEach((item: Item) => {
        const key = formName ? `${formName}[${item.name}]` : item.name;
        if (['string', 'number', 'boolean'].includes(typeof item.value)) {
            formData.append(key, String(item.value));
        }
        if ('object' === typeof item.value && item.value) {
            convertModelToFormData(item.value, key, formData);
        }
    });
    return formData;
}

export function objectIsEmpty(obj: object): boolean {
    return Object.values(obj).every(value => !value);
}

export function copyTextClipboard(text): boolean {
    const textarea = document.createElement("textarea");
    textarea.textContent = (text);
    textarea.style.position = "fixed";  // Prevent scrolling to bottom of page in Microsoft Edge.
    document.body.appendChild(textarea);
    textarea.select();
    try {
        return document.execCommand("copy");  // Security exception may be thrown by some browsers.
    } catch (ex) {
        console.warn("Copy to clipboard failed.", ex);
        return false;
    } finally {
        document.body.removeChild(textarea);
    }
    return true;
}

export const compareIds = (a, b) => {
    return String(a) === String(b);
};

export function calcLicenses(i_licenses: number, i_accountStatus: null | 4) {
    let price = 2.8 * i_licenses + 17.19 * Math.pow(i_licenses, 0.5);
    if (i_accountStatus == 4) {
        price = Math.min(600, price);
    }
    const license: any = parseFloat(price / i_licenses as any).toFixed(2);
    const licenses: any = parseFloat(price as any).toFixed(2);
    return {license, licenses};
}


export function reorderPages(i_pages, i_playlists): object {
    const final = {};
    i_playlists.forEach((i_playlist) => {
        i_pages.filter((i_page) => {
            return i_playlist.id === i_page.channel;
        }).sort((a, b) => {
            return a.start - b.start;
        }).reduce((accum, item) => {
            accum === 0 ? item.start = 0 : item.start = accum;
            accum = accum + item.duration;
            return accum;
        }, 0);
        final[`playlist_${i_playlist.id}`] = i_playlist;
        final[`pages_${i_playlist.id}`] = [];
    });
    i_pages.forEach((a) => {
        final[`pages_${a.channel}`].push(a);
        final[`pages_${a.channel}`].sort((c, d) => c.start - d.start);
    });
    return final;
}

export function formatAccountDetails(i_orderForm: IOrderForm): IAccountDetails {
    return {
        Account: {
            Recurring: [
                {
                    $: {
                        id: '',
                        lastPayment: '',
                        paymentStatus: '',
                        recurringMode: '',
                    }
                }
            ],
            Contact: [
                {
                    $: {
                        firstName: '',
                        lastName: '',
                        email: '',
                        workPhone: '',
                        cellPhone: '',
                        address1: '',
                        city: '',
                        state: '',
                        contry: '',
                        zipCode: '',
                    }
                }
            ],
            Shipping: [
                {
                    $: {
                        firstName: i_orderForm.shippingFirstName,
                        lastName: i_orderForm.shippingLastName,
                        address1: i_orderForm.shippingAddress1,
                        address2: i_orderForm.shippingAddress2,
                        city: i_orderForm.shippingCity,
                        state: i_orderForm.shippingState,
                        country: i_orderForm.shippingCountry,
                        zipCode: i_orderForm.shippingZip,
                        workPhone: i_orderForm.tel,
                        cellPhone: i_orderForm.tel,
                        email: i_orderForm.email
                    }
                }
            ],
            Billing: [
                {
                    $: {
                        firstName: i_orderForm.billingFirstName,
                        lastName: i_orderForm.billingLastName,
                        cardType: String(i_orderForm.card_type),
                        cardNumber: i_orderForm.card_number,
                        securityCode: String(i_orderForm.security_code),
                        expirationMonth: String(i_orderForm.expiration_month),
                        expirationYear: String(i_orderForm.expiration_year),
                        workPhone: i_orderForm.tel,
                        email: i_orderForm.mail,
                        address1: i_orderForm.billingAddress1,
                        address2: i_orderForm.billingAddress2,
                        cellPhone: i_orderForm.tel,
                        city: i_orderForm.billingCity,
                        country: i_orderForm.billingCountry,
                        state: i_orderForm.billingState,
                        zipCode: i_orderForm.billingZip
                    }
                }
            ]
        }
    };
}

export function convertToXML(data, asXml): XMLDocument | any {
    const a = JSON.stringify(data);
    const b = a.replace(/"\$":/ig, "\"_attributes\":");
    const c = JSON.parse(b);
    if (asXml) {
        return convert.js2xml(c, {compact: true, ignoreComment: true, spaces: 0, attributeValueFn: encodeHTML});
    } else {
        return c;
    }
}

export function preprocessSceneRecord(scene, i_sceneId, i_studioId, i_command: ICRUDCommand, lastModified = null): ICRUDRecord {
    const c = convertToXML(scene, false);

    if ('change_type' in c) {
        delete c['change_type'];
    }

    if (c.Player._attributes.guides) {
        try {
            delete c.Player._attributes.guides;
        } catch (e) {
            Lib.DevShow('problem removing guides on save...');
        }
    }

    if (lastModified) {
        c.Player._attributes.lastModified = lastModified;
    }

    /** replace <_>, </_>, <_/> which is in the case of QR component due to compatability with old SignagePlayer **/
    let updatedData = convert.js2xml(c, {compact: true, ignoreComment: true, spaces: 0, attributeValueFn: encodeHTML});

    const tmpXML = updatedData.replace(/<_>/g, '').replace(/<_\/>/g, '');
    if (updatedData.length !== tmpXML.length) {
        updatedData = tmpXML.replace(/<\/_>/g, '');
    }

    /** Update scene */
    const saveRecord: ICRUDRecord = {
        studioId: i_studioId,
        command: i_command,
        primary: 'player_data_id',
        table: 'player_data',
        body: {
            player_data_value: updatedData
        },
        id: i_sceneId
    };

    return saveRecord;
}

export function getSceneName(i_sceneId, i_storeSelector: StoreSelectorsService) {
    let name;
    i_storeSelector.getScene(i_sceneId)
        .pipe(take(1))
        .subscribe((i_scenePlayer: IPlayerProps) => {
            name = i_scenePlayer?.Player?.$?.label;
        });
    return name;
}

export function encodeHTML(attributeValue) {
    return attributeValue.replace(/&quot;/g, '"')  // convert quote back before converting amp
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

export function getResourceType(i_resourceId, i_storeSelector: StoreSelectorsService) {
    let type;
    i_storeSelector.getResource(i_resourceId)
        .pipe(take(1))
        .subscribe((i_resource: IResource) => {
            type = i_resource.resource_type;
        });
    return type;
}

export function getResourceName(i_resourceId, i_storeSelector: StoreSelectorsService) {
    let name;
    i_storeSelector.getResource(i_resourceId)
        .pipe(take(1))
        .subscribe((i_resource: IResource) => {
            name = i_resource.resource_name;
        });
    return name;
}

export function createPageData(i_page, storeSelector: StoreSelectorsService): IPageBase {
    if (i_page.originalData)
        return _.cloneDeep(i_page.originalData);
    let result: IPage = null;
    switch (i_page.type) {
        case 'scene': {
            result = {
                $: {
                    page: "page",
                    type: "scene",
                    ...i_page.duration && {duration: i_page.duration}
                },
                Player: [{$: {src: i_page.id}}]
            };
            break;
        }
        default: {
            storeSelector.getResource(i_page.id)
                .pipe(take(1))
                .subscribe((i_resource: IResource) => {
                    const type = i_resource.resource_type.toLowerCase();
                    const player = storeSelector.getBlockLabelByFileType(type).id;
                    result = {
                        $: {
                            page: "page",
                            type: "resource",
                            ...i_page.duration && {duration: i_page.duration}
                        },
                        Player: [{
                            $: {player, id: i_page.id ? i_page.id : _.uniqueId('__')},
                            Data: [{Resource: [{$: {resource: i_page.id}}]}]
                        }]
                    };
                });
        }
    }
    return result;
}

export function saveDownloadedData(filename, data) {
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(data));
    element.setAttribute('download', filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
}

export function getTotalLengthPlaylist(pages: IPage[]) {
    let len = 0;
    if (pages && pages.length > 0) {
        pages.forEach((_page) => {
            if (_page.$['duration']) {
                len += parseFloat(_page.$['duration']);
            } else {
                len += 10;
            }
        });
    }
    return len;
}

export function isDuplicateInArray(arr) {
    return _.uniq(arr).length !== arr.length;
}

export function checkValidLink(link) {
    const expression = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gi;
    const regex = new RegExp(expression);
    return link.match(regex);
}

export function getExtension(str) {
    const re = /(?:\.([^.]+))?$/;
    return re.exec(str)[1];
}

export function moveInArray(from, to, ...a) {
    return from === to ? a : (a.splice(to, 0, ...a.splice(from, 1)), a);
}

export function capitalize(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function pad(str, length) {
    while (str.length < length) {
        str = '0' + str;
    }
    return str;
}

export function getRandomColor() {
    return (
        pad(getRandomNum(0, 255).toString(16), 2) +
        pad(getRandomNum(0, 255).toString(16), 2) +
        pad(getRandomNum(0, 255).toString(16), 2)
    );
}

export function getRandomNum(min, max) {
    return Math.round(Math.random() * (max - min)) + min;
}

export function getRandomLeftTop() {
    const offset = 50;
    return {
        left: getRandomNum(0 + offset, 300 - offset),
        top: getRandomNum(0 + offset, 300 - offset)
    };
}

export function isNumericValue(value: any): boolean {
    return /^-?\d+$/.test(value);
}

export function isStringBasedJson(value: any): boolean {
    if (typeof value === 'string' || value instanceof String) {
        if (value.indexOf('}') === -1) {
            return false;
        }
        try {
            // @ts-expect-error: Let's ignore
            const p = JSON.parse(value);
            return true;
        } catch (e) {
            return false;
        }
    }
    return false;
}

export function isCartEmpty(cart: ICart): boolean {
    if (!cart || !cart.total) return true;
    const cartTotal = String(cart.total).replace(/\$/ig, '').replace(/,/ig, '').replace(/0/ig, '').replace(/\./ig, '');
    if (_.isEmpty(cartTotal)) return true;
    return isStringNullOrUndefOrEmpty(cart.cartLocation);
}

/** cart ship to locations are stored as "STATE:CARRIER" (e.g. "CA:UPS"); "INT:USPS" = international.
 *  The store ships to US addresses only, so an INT location must never unlock checkout **/
export function isInternationalShipping(cartLocation: string): boolean {
    if (isStringNullOrUndefOrEmpty(cartLocation)) return false;
    return String(cartLocation).split(':')[0].trim().toUpperCase() === 'INT';
}

export function isProductInUrl(navigationEnd): boolean {
    if (!navigationEnd || !navigationEnd.url) return false;
    return !!navigationEnd.url.match(/list\/(.*)/) || !!navigationEnd.url.match(/charge\/(.*)/);
}

export function moveElementInArray(array, fromIndex, toIndex) {
    array.splice(toIndex, 0, array.splice(fromIndex, 1)[0]);
}

export function isMediaSignageInc(value): boolean {
    if (isNullOrUndefOrEmpty(value)) return false;
    if (typeof value === 'string' || value instanceof String) {
        value = value.toLowerCase();
        if (value.indexOf('digitalsignage.com') > -1
            || value.indexOf('signage.me') > -1
            || value.indexOf('mediasignage.com') > -1) {
            return true;
        }
    } else {
        return false;
    }
}

export function isHostLite(service) {
    const origin = window.location.toString();
    const pattern = '^(https|http)(:\/\/)(.*?)\/';
    const re: any = new RegExp(pattern);
    const server: any = origin.match(re)[3];
    if (server.match(/gsignage.com/i) || server.match(/signage.me/i) || server.match(/localhost/i) || server.match(/digitalsignage.com/i))
        return `${service}/studioweb/UserLogin`;
    // return '{this.appSettings.globalService}/_studiolite-dist/studiolite.html';
    return `${origin}/_studiolite-dist/studiolite.html`;
}

export function isHostLocal() {
    return window.location.href.indexOf('localhost') > -1;
}

export function isGalaxy() {
    return window.location.href.indexOf('galaxy') > -1;
}

export function isHostFeatures() {
    return window.location.href.indexOf('features') > -1;
}

export function isHostDev() {
    return window.location.href.indexOf('dev') > -1;
}

export function isSubscribersMode() {
    return window.location.href.indexOf('shopping-cart/subscriber/') > -1;
}

export function isEmbeddedWebSite() {
    return window.location.href.indexOf('/shopping-cart/') > -1;
}

export function isHostDevOrLocal() {
    return window.location.href.indexOf('dev') > -1 || window.location.href.indexOf('localhost') > -1;
}

/**
 * The single environment gate for the legacy-account migration feature (legacy Studio Pro/Lite ->
 * SignStudio). Every migration entry point (root redirect, post-login navigate, the /migrate route
 * guard, the page itself, the download-card button, the Account > Migration revert tab) goes here.
 *
 * The feature is now RELEASED to all environments (including production), so this returns true
 * unconditionally. Access is still governed by isMigrationPermitted() / the authoritative server-side
 * DataConnection.isMigrationAllowed gate (legacy account, direct/FREE/PLUS, or reseller-granted
 * allow_migration) — this function no longer restricts by host.
 */
export function isMigrationAvailable(): boolean {
    return true;
}

/**
 * Whether this account may migrate (legacy Studio -> SignStudio). The single client-side gate,
 * shared by every entry point (root redirect, /migrate route guard, the download-card button):
 *  - must be a legacy account (studioType Pro/Lite) — nothing to migrate otherwise,
 *  - direct/non-reseller accounts always migrate,
 *  - FREE and PLUS accounts always migrate (pricingMethod 0/2),
 *  - only ENTERPRISE-tier white-label accounts (under a reseller, pricingMethod 1/3) are gated on the
 *    reseller-granted allow_migration.
 * Mirrors the server-side DataConnection.isMigrationAllowed gate — the server stays authoritative;
 * this only decides whether to SHOW the UI. Does NOT include the dev-only isMigrationAvailable() check;
 * callers combine the two.
 */
/**
 * The migration PERMISSION rule, independent of account state — the single flag gates BOTH migrate
 * and revert. Shared by isAccountMigratable (legacy -> migrate) and the revert visibility (migrated
 * -> revert). Direct / not-reseller, FREE and PLUS accounts are always permitted; only ENTERPRISE-tier
 * white-label accounts (under a reseller, pricingMethod 1/3) need the reseller-granted allow_migration.
 * Mirrors the server-side DataConnection.isMigrationAllowed gate.
 */
export function isMigrationPermitted(user: IUser): boolean {
    if (!user) return false;
    if (!user.enterprise) return true;   // direct / not under a reseller
    if (user.pricingMethod === EnumPricingMethod.FREE || user.pricingMethod === EnumPricingMethod.PLUS) return true;
    return !!user.allowMigration;
}

export function isAccountMigratable(user: IUser): boolean {
    if (!user) return false;
    return isLegacyAccount(user) && isMigrationPermitted(user);
}

/**
 * Whether this is a legacy (pre-SignStudio) account — studioType Pro or Lite. A migrated or
 * native Sign account has a non-legacy studioType. Legacy-only UI (e.g. theme, soft keyboard)
 * should show only when this is true.
 */
export function isLegacyAccount(user: IUser): boolean {
    if (!user) return false;
    return user.studioType === EnumStudioType.PRO || user.studioType === EnumStudioType.LITE;
}

/**
 * Legacy player versions at or below this are "critical": they cannot display the migrated/SignStudio
 * scenes and must be reinstalled with SignPlayer. Versions above it are merely "should upgrade".
 * Single source of truth for the threshold — used by the players grid, the migrate page and the
 * account migration panel.
 */
export const LEGACY_CRITICAL_MAX_MAJOR = 6;
export const LEGACY_CRITICAL_MAX_MINOR = 2;   // i.e. <= 6.2

/**
 * True when a legacy player's reported app version (e.g. "6.4.10") is at or below the critical
 * threshold (<= 6.2). Compares major AND minor as integers so "6.10.x" is correctly treated as
 * ABOVE 6.2 (a naive parseFloat("6.10") === 6.1 would wrongly flag it). An empty/unparseable
 * version returns false (unknown → not critical; callers treat it as the softer "upgrade" case).
 */
export function isLegacyPlayerVersionCritical(appVersion: string): boolean {
    const parts = String(appVersion ?? '').split('.');
    const major = parseInt(parts[0], 10);
    if (isNaN(major)) return false;
    const minor = parseInt(parts[1] ?? '0', 10) || 0;
    return major < LEGACY_CRITICAL_MAX_MAJOR
        || (major === LEGACY_CRITICAL_MAX_MAJOR && minor <= LEGACY_CRITICAL_MAX_MINOR);
}

export function popupWindow({url, title, w, h}, scrollbars = 'yes', resizable = 'yes') {
    console.log('opening popup ' + url);
    // Fixes the dual-screen position most browsers
    const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;
    const width = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width;
    const height = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height;
    const systemZoom = width / window.screen.availWidth;
    const left = (width - w) / 2 / systemZoom + dualScreenLeft;
    const top = (height - h) / 2 / systemZoom + dualScreenTop;
    const newWindow = window.open(url, title, `toolbar=no,menubar=no,resizable=${resizable},status=yes,scrollbars=${scrollbars},width=${w / systemZoom},height=${h / systemZoom},top=${top},left=${left}`);
    if (window?.focus && newWindow) newWindow.focus();
    return newWindow;
}

export function getGuidelineFromPattern(pType: number, w: number, h: number) {
    let guidelines = {
        horizontal: [],
        vertical: []
    };
    let hidelines = {
        horizontal: [],
        vertical: []
    };
    const MAX_GUIDELINES = 9999;

    switch (pType) {
        case 1:
            guidelines = {horizontal: [], vertical: [0.5 * w]};
            break;
        case 2:
            guidelines = {horizontal: [0.5 * h], vertical: []};
            break;
        case 3:
            guidelines = {horizontal: [0.75 * h], vertical: []};
            break;
        case 4:
            guidelines = {horizontal: [0.5 * h], vertical: [0.5 * w]};
            break;
        case 5:
            guidelines = {horizontal: [0.5 * h], vertical: [0.25 * w]};
            break;
        case 6:
            guidelines = {horizontal: [0.75 * h], vertical: [0.5 * w]};
            hidelines = {
                vertical: [{x1: '1', y1: '1', x2: '1', y2: MAX_GUIDELINES + ''}],
                horizontal: [{x1: '1', y1: '1', x2: MAX_GUIDELINES + '', y2: '1'}]
            };
            break;
        case 7:
            guidelines = {horizontal: [0.1 * h, 0.9 * h], vertical: []};
            break;
        case 8:
            guidelines = {horizontal: [], vertical: [0.1 * w, 0.9 * w]};
            break;
        case 9:
            guidelines = {horizontal: [0.1 * h, 0.9 * h], vertical: [0.1 * w, 0.9 * w]};
            break;
        case 10:
            guidelines = {horizontal: [], vertical: [1 / 3 * w, 2 / 3 * w]};
            break;
        case 11:
            guidelines = {horizontal: [1 / 3 * h, 2 / 3 * h], vertical: []};
            break;
        case 12:
            guidelines = {horizontal: [1 / 3 * h, 2 / 3 * h], vertical: [1 / 4 * w, 2 / 4 * w, 3 / 4 * w]};
            hidelines = {
                vertical: [{x1: '2', y1: '1', x2: '2', y2: '2'}],
                horizontal: [
                    {x1: '3', y1: '1', x2: MAX_GUIDELINES + '', y2: '1'},
                    {x1: '0', y1: '1', x2: '2', y2: '1'},
                    {x1: '0', y1: '1', x2: '1', y2: '1'},
                    {x1: '3', y1: '2', x2: MAX_GUIDELINES + '', y2: '2'}]
            };
            break;
    }

    return {
        guidelines,
        hidelines
    };
}

// export function formatBytesToMegabytes(i_value): number {
//     return i_value / 1024;
//     // const i = Math.floor(Math.log(i_value) / Math.log(1024));
//     // const b = parseFloat((i_value / Math.pow(1024, i)).toFixed(2));
//     // return i_value = b * 1000;
// }

export function checkImageType(type) {
    if (!type) return false;
    const imageTypes = ['png', 'jpeg', 'gif', 'jpg', 'bmp', 'svg'];
    if (imageTypes.indexOf(type.toLowerCase()) > -1) return true;
    return false;
}

export function checkLockedScene(lockedRecord, sceneId, studioId) {
    let lockStatus;

    if (!lockedRecord || !sceneId || !studioId) {
        return;
    }

    if (lockedRecord) {
        const rec = lockedRecord.lockedRecords.find((r) => r.table === 'player_data' && r.tableId == sceneId);
        if (rec) {
            if (rec.lockId != studioId) {
                lockStatus = 'locked';
            } else {
                lockStatus = 'edit';
            }
        }
    }

    return lockStatus;
}

/* fix for sometimes saving schedule_events is null which screws up studio load */
export function isScheduleEventsNull(i_value): object {
    if (isNullOrUndefOrEmpty(i_value)) return {};
    return i_value;
}

export function noWhitespaceValidator(control: FormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : {'whitespace': true};
}

export async function convertToFileFromImageUrlOrBase64(img, fileName = null) {
    const response = await fetch(img);
    const data = await response.blob();
    const metadata = {
        type: data.type
    };

    let fileType = data.type.slice(6);
    if (fileType === 'jpeg') {
        fileType = 'jpg';
    }
    if (!fileName) {
        fileName = `image_${new Date().getTime()}`;
    }

    const newFileName = `${fileName}.${fileType}`;

    // return new File([data], newFileName, metadata);
    return {
        fileObj: new File([data], newFileName, metadata),
        fileType,
        fileName
    };
}

export async function convertImageUrlToBase64(url: string) {
    const data = await fetch(url);
    const blob = await data.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
            const base64data = reader.result;
            resolve(base64data);
        };
        reader.onerror = reject;
    });
}

export function divAlert(mymsg, mymsecs) {
    const myelement = document.createElement("div");
    myelement.setAttribute("style", "z-index: 10000; background-color: grey;color:black; width: 150px;height: 200px;position: absolute;top:0;bottom:0;left:0;right:0;margin:auto;border: 4px solid black;font-family:arial;font-size:25px;font-weight:bold;display: flex; align-items: center; justify-content: center; text-align: center;");
    myelement.innerHTML = mymsg;
    setTimeout(() => {
        myelement.parentNode.removeChild(myelement);
    }, mymsecs);
    document.body.appendChild(myelement);
}

export function isValidUrl(string) {
    try {
        new URL(string);
        return true;
    } catch (err) {
        return false;
    }
}

export function fileTailName(str, length = 1) {
    if (!str) return '';
    const parts = String(str).split('/');
    if (parts.length > 0) {
        return parts.slice(0 - length, parts.length).join('/');
    }
    return str;
}

export const validateCCNumber: ValidatorFn = (
        control: AbstractControl,
    ): ValidationErrors | null => {
        if (Validators.required(control) !== undefined && Validators.required(control) !== null) {
            return {ccNumber: true};
        }
    
        const num = control.value.toString().replace(/\s+|-/g, '');
    
        if (!/^\d+$/.test(num)) {
            return {ccNumber: true};
        }
    
        const card = CreditCard.cardFromNumber(num);
    
        if (!card) {
            return {ccNumber: true};
        }
    
        if (card.length.includes(num.length) && (card.luhn === false || CreditCard.luhnCheck(num))) {
            return null;
        }
    
        const upperlength = card.length[card.length.length - 1];
        if (num.length > upperlength) {
            const registeredNum = num.substring(0, upperlength);
            if (CreditCard.luhnCheck(registeredNum)) {
                return null;
            }
        }
    
        return {ccNumber: true};
    }

/**
 * Clean and normalize prompt for better cache hit rates
 * - Convert to lowercase
 * - Remove carriage returns and newlines
 * - Trim leading and trailing whitespace
 * - Remove special characters (!, ?, etc.)
 * - Replace multiple spaces with single space
 * - Remove extra whitespace
 */
export function cleanAiPrompt(prompt: string): string {
    return prompt
        .replace(/[\r\n]/g, "")                 // Remove carriage returns and newlines
        .trim()                                 // Trim leading/trailing whitespace
        .replace(/[!\[\]{}]/g, "")            // Remove special characters
        .replace(/\s+/g, " ")                   // Replace multiple spaces with single space
        .trim();                                // Final trim after space normalization
}   

export function isValidStr(str: string): boolean {
    if (typeof str !== 'string' || !str || !str.trim()) return false;
    return true;
}

export function isSkipLoadingDataRoute() {
    const pathname = window.location.pathname;

    // Split the pathname by '/' to get an array of segments, removing empty strings
    const segments = pathname.split('/').filter(Boolean);

    // Get the last element of the array
    const lastPathSegment = segments.pop();

    return SKIP_LOADING_ROUTES.includes(lastPathSegment);
}
